
import React, { useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { FormInput } from '@/components/forms';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ExamType, ExamResult } from '@/utils/exam/types';
import { UseFormRegister, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';

interface ExamDetailsFieldsProps {
  register: UseFormRegister<Partial<ExamResult>>;
  errors: FieldErrors<Partial<ExamResult>>;
  setValue: UseFormSetValue<Partial<ExamResult>>;
  watch: UseFormWatch<Partial<ExamResult>>;
  examTypes: ReadonlyArray<{ readonly value: ExamType; readonly label: string }>;
  formSubmitted?: boolean;
  touchedFields?: Record<string, boolean>;
  studentPrograms?: any[];
}

const ExamDetailsFields = ({ 
  register, 
  errors, 
  setValue, 
  watch,
  examTypes,
  formSubmitted = false,
  touchedFields = {},
  studentPrograms = []
}: ExamDetailsFieldsProps) => {
  // Only show errors if form was submitted or field was touched and then blurred
  const shouldShowError = (fieldName: keyof ExamResult) => 
    (formSubmitted || touchedFields[fieldName]) && errors[fieldName];
  
  // Watch program title to update related fields
  const watchProgramTitle = watch('programTitle');
  
  // Auto-populate program category when program title changes
  useEffect(() => {
    if (watchProgramTitle && studentPrograms?.length > 0) {
      const selectedProgram = studentPrograms.find(program => program.title === watchProgramTitle);
      if (selectedProgram) {
        // Set program category if available
        if (selectedProgram.category) {
          setValue('subject', selectedProgram.category);
        }
      }
    }
  }, [watchProgramTitle, studentPrograms, setValue]);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="examType" className="text-base">Exam Type</Label>
          <Select
            defaultValue={watch('examType') || 'test'}
            onValueChange={(value) => setValue('examType', value as ExamType)}
          >
            <SelectTrigger id="examType" className="h-12">
              <SelectValue placeholder="Select exam type" />
            </SelectTrigger>
            <SelectContent>
              {examTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="examDate" className="text-base">Exam Date</Label>
          <Input
            id="examDate"
            type="date"
            className="h-12"
            {...register('examDate')}
          />
          {shouldShowError('examDate') && (
            <p className="text-xs text-red-500">{errors.examDate?.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="programTitle" className="text-base">Program</Label>
        <Select
          value={watch('programTitle') || ''}
          onValueChange={(value) => setValue('programTitle', value)}
        >
          <SelectTrigger id="programTitle" className="h-12">
            <SelectValue placeholder="Select student program" />
          </SelectTrigger>
          <SelectContent>
            {studentPrograms && studentPrograms.length > 0 ? (
              studentPrograms.map((program, index) => (
                <SelectItem key={index} value={program.title}>
                  {program.title} ({program.category})
                </SelectItem>
              ))
            ) : (
              <SelectItem value="none" disabled>No programs available</SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="subject" className="text-base">Subject</Label>
        <FormInput
          id="subject"
          name="subject"
          inputType="text"
          className="h-12"
          {...register('subject')}
          placeholder="e.g., Mathematics, Computer Science"
        />
        {shouldShowError('subject') && (
          <p className="text-xs text-red-500">{errors.subject?.message}</p>
        )}
        <p className="text-xs text-muted-foreground">This will automatically populate based on program when available</p>
      </div>
    </>
  );
};

export default ExamDetailsFields;
